import { useState, useContext } from "react";
import { AxiosResponse } from "axios";
import {
  categoryContextType,
  categoryDataTypes,
  iTypeCategory,
  notificationContextTypes,
  typeApiResponse,
  typeCategoryOperationDto,
} from "../../utils/typeDefs";
import { apiUrls } from "../../utils/apiUrls";
import { Dropdown, Button } from "rsuite";
import {
  Plus,
  Edit,
  Block,
  SortUp,
  SortDown,
  Trash,
  DocPass,
  ArrowDownLine,
  ArrowUpLine,
} from "@rsuite/icons";
import InfoOutlineIcon from "@rsuite/icons/InfoOutline";
import Input from "rsuite/Input";
import styles from "./categoryNode.module.scss";
import ReusableModal from "../generic/modal/reusableModal.component";
import { CategoryContext } from "../../core/context/categoryContextProvider";
import { postData, putData, deleteData } from "../../utils/reusables";
import { NotificationContext } from "../../core/context/notificationContextProvider";

type categoryNodeTypes = {
  categoryData: iTypeCategory;
};

const CategoryNode = ({ categoryData }: categoryNodeTypes) => {
  const { id, title, isActive } = categoryData;

  const { setCategoryData } = useContext(
    CategoryContext
  ) as categoryContextType;
  const { triggerNotification } = useContext(
    NotificationContext
  ) as notificationContextTypes;

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [updatingTitle, setUpdatingTitle] = useState<string>("");
  const [currentDropdownIcon, setCurrentDropdownIcon] = useState<JSX.Element>(
    <ArrowDownLine />
  );
  const [currentChild, setCurrentChild] = useState<JSX.Element | null>(null);
  const [disableModalBtn, setDisableModalBtn] = useState<boolean>(false);

  const handleTitleChange = (value: string) => {
    setUpdatingTitle(value);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setModalType(null);
    setCurrentChild(null);
    setOpenModal(false);
  };

  const handleConfirm = () => {
    setDisableModalBtn(true);

    function handleData(
      fullUrl: string,
      data: typeCategoryOperationDto,
      req: Function
    ) {
      setCategoryData([]);
      req(fullUrl, data ? data : null).then(
        (result: AxiosResponse<categoryDataTypes & typeApiResponse>) => {
          setCategoryData([result.data.data]);
          triggerNotification(
            result.data.succeeded,
            result.data.succeeded
              ? "Successfully updated category list"
              : "Failed to update category list"
          );
        }
      );
    }

    switch (modalType) {
      case "add":
        handleData(
          apiUrls.category.base,
          {
            ParentId: id,
            Title: updatingTitle,
          },
          postData
        );
        break;
      case "edit":
        handleData(
          apiUrls.category.base,
          {
            ParentId: id,
            Title: updatingTitle,
          },
          putData
        );
        break;
      case "activate":
        handleData(
          apiUrls.category.active,
          {
            Id: id,
          },
          postData
        );
        break;
      case "inactivate":
        handleData(
          apiUrls.category.inactive,
          {
            Id: id,
          },
          postData
        );
        break;
      case "moveUp":
        handleData(
          apiUrls.category.moveUp,
          {
            Id: id,
          },
          postData
        );
        break;
      case "moveDown":
        handleData(
          apiUrls.category.moveDown,
          {
            Id: id,
          },
          postData
        );
        break;
      case "delete":
        setCategoryData([]);
        deleteData(apiUrls.category.deleteNode(id)).then(
          (result: AxiosResponse<categoryDataTypes & typeApiResponse>) => {
            setCategoryData([result.data.data]);
            triggerNotification(
              result.data.succeeded,
              result.data.succeeded
                ? "Successfully updated category list"
                : "Failed to update category list"
            );
          }
        );
        break;
      default:
        return;
    }

    handleCloseModal();
  };

  const renderDropdownBtn = (props: any, ref: any) => {
    return (
      <Button className={`d-flex`} size="sm" {...props} ref={ref}>
        <span>Action</span>{" "}
        <span className="ms-auto">{currentDropdownIcon}</span>
      </Button>
    );
  };

  const add = (
    <div>
      <Input onChange={handleTitleChange} placeholder="Add Title" />
    </div>
  );

  const edit = (
    <div>
      <Input onChange={handleTitleChange} placeholder="Edit Title" />
    </div>
  );

  const active = <p>Are you sure you want to active {title}?</p>;
  const inactive = <p>Inactive {title}?</p>;
  const moveUp = <p>Move up {title}?</p>;
  const moveDown = <p>Move down {title}?</p>;
  const deleteItem = (
    <div className="d-flex flex-column align-items-center">
      <InfoOutlineIcon className="mb-2 delete-alert-icon" />
      <p>Are you sure you want to delete {title}?</p>
    </div>
  );

  return (
    <div className="row h-100">
      <div className="col-lg-10 col-6">
        <p className={`  ${styles.categoryText}`}>
          <span className={`${isActive ? styles.active : styles.inactive}`}>
            {title}
          </span>
        </p>
      </div>

      <div className="col-lg-2 col-6">
        {id === null && (
          <Dropdown
            onOpen={() => setCurrentDropdownIcon(<ArrowUpLine />)}
            onClose={() => setCurrentDropdownIcon(<ArrowDownLine />)}
            renderToggle={renderDropdownBtn}
            className="w-100"
            title="Action"
          >
            <Dropdown.Item
              disabled={!isActive}
              onClick={() => {
                setModalType("add");
                setCurrentChild(add);
                handleOpenModal();
              }}
            >
              <Plus /> <span className="pe-2">Add</span>
            </Dropdown.Item>
          </Dropdown>
        )}
        {id !== null && (
          <Dropdown
            onOpen={() => setCurrentDropdownIcon(<ArrowUpLine />)}
            onClose={() => setCurrentDropdownIcon(<ArrowDownLine />)}
            renderToggle={renderDropdownBtn}
            className="w-100"
            title="Action"
          >
            <Dropdown.Item
              disabled={!isActive}
              onClick={() => {
                setModalType("add");
                setCurrentChild(add);
                setDisableModalBtn(false);
                handleOpenModal();
              }}
            >
              <Plus /> <span className="pe-2">Add</span>
            </Dropdown.Item>
            <Dropdown.Item
              disabled={!isActive}
              onClick={() => {
                setModalType("edit");
                setCurrentChild(edit);
                setDisableModalBtn(false);
                handleOpenModal();
              }}
            >
              <Edit /> <span className="pe-2">Edit</span>
            </Dropdown.Item>
            {!isActive ? (
              <Dropdown.Item
                onClick={() => {
                  setModalType("activate");
                  setCurrentChild(active);
                  setDisableModalBtn(false);
                  handleOpenModal();
                }}
              >
                <DocPass /> <span className="pe-2">Activate</span>
              </Dropdown.Item>
            ) : (
              <Dropdown.Item
                onClick={() => {
                  setModalType("inactivate");
                  setCurrentChild(inactive);
                  setDisableModalBtn(false);
                  handleOpenModal();
                }}
              >
                <Block /> <span className="pe-2">Inactivate</span>
              </Dropdown.Item>
            )}
            <Dropdown.Item
              onClick={() => {
                setModalType("moveUp");
                setCurrentChild(moveUp);
                setDisableModalBtn(false);
                handleOpenModal();
              }}
            >
              <SortUp /> <span className="pe-2">Move Up</span>
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => {
                setModalType("moveDown");
                setCurrentChild(moveDown);
                setDisableModalBtn(false);
                handleOpenModal();
              }}
            >
              <SortDown /> <span className="pe-2">Move Down</span>
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => {
                setModalType("delete");
                setCurrentChild(deleteItem);
                setDisableModalBtn(false);
                handleOpenModal();
              }}
            >
              <Trash /> <span className="pe-2">Delete</span>
            </Dropdown.Item>
          </Dropdown>
        )}
      </div>

      <ReusableModal
        modalTitle={title}
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        children={currentChild}
        handleConfirm={handleConfirm}
        disableModalBtn={disableModalBtn}
      />
    </div>
  );
};

export default CategoryNode;
